/**
 * sl-hle4.14: Custom-components Beta — settings audit surface.
 *
 * The custom-app custom-components system (sl-y0sj.6) is a CLOSED
 * typed registry: `cta_button`, `embed_video`, `faq_block`. Authors
 * add components inline inside the doc editor — there is no arbitrary
 * TypeScript source, no plugin hosting, no preview iframe. Settings
 * therefore only need a Beta-disclaimer + a read-only audit view of
 * what each custom app currently uses, plus a pointer to the
 * editor where edits happen.
 *
 * Visibility:
 *   - Plan-gated via `useFeatureGate('custom_apps.custom_components_beta')`.
 *     If the workspace's plan doesn't include the gate, the section is
 *     not rendered at all (zero footprint).
 *
 * SOLID:
 *   - S: One section, one concern (Beta-disclaimer + audit summary).
 *   - D: Reads `customAppsQuery` for the per-view config; never
 *     touches the customComponents validator directly. Editor lives in
 *     the doc surface.
 */
import { Button, Menu, MenuItem } from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { GraphQLService } from '@affine/core/modules/cloud';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import { customAppsQuery, customAppViewerStateQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { type FC, useEffect, useMemo, useState } from 'react';

const KNOWN_COMPONENT_TYPES = [
  'cta_button',
  'embed_video',
  'faq_block',
] as const;
type ComponentType = (typeof KNOWN_COMPONENT_TYPES)[number];

interface ViewListItem {
  id: string;
  title: string | null;
  slug: string;
  isPublished: boolean;
}

function countComponentsByType(
  viewConfig: Record<string, unknown> | null
): Record<ComponentType, number> {
  const counts: Record<ComponentType, number> = {
    cta_button: 0,
    embed_video: 0,
    faq_block: 0,
  };
  if (!viewConfig) return counts;
  const components = viewConfig.customComponents;
  if (!Array.isArray(components)) return counts;
  for (const entry of components) {
    if (typeof entry !== 'object' || entry === null) continue;
    const type = (entry as { type?: unknown }).type;
    if (
      typeof type === 'string' &&
      (KNOWN_COMPONENT_TYPES as readonly string[]).includes(type)
    ) {
      counts[type as ComponentType] += 1;
    }
  }
  return counts;
}

export const ComponentsSection: FC = () => {
  const t = useI18n();
  const { isFeatureAllowed } = useFeatureGate();
  const graphqlService = useService(GraphQLService);
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;

  const [views, setViews] = useState<ViewListItem[] | null>(null);
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewConfig, setViewConfig] = useState<Record<string, unknown> | null>(
    null
  );
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  const allowed = isFeatureAllowed('custom_apps.custom_components_beta');

  const loadViews = useAsyncCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await graphqlService.gql({
        query: customAppsQuery,
        variables: { workspaceId },
      });
      const list = result.customApps
        .filter(v => v.isPublished)
        .map(v => ({
          id: v.id,
          title: v.title,
          slug: v.slug,
          isPublished: v.isPublished,
        }));
      setViews(list);
      if (list.length > 0 && !selectedViewId) {
        setSelectedViewId(list[0].id);
      }
    } catch (err) {
      const friendly = UserFriendlyError.fromAny(err);
      setError(friendly.message ?? 'Failed to load custom apps');
      setViews([]);
    } finally {
      setIsLoading(false);
    }
  }, [graphqlService, workspaceId, selectedViewId]);

  useEffect(() => {
    if (allowed) {
      loadViews();
    }
  }, [allowed, loadViews]);

  const loadViewerState = useAsyncCallback(
    async (viewId: string) => {
      setIsLoadingConfig(true);
      setConfigError(null);
      try {
        const result = await graphqlService.gql({
          query: customAppViewerStateQuery,
          variables: { workspaceId, customAppId: viewId },
        });
        const cfg = result.customAppViewerState?.viewConfig as
          | Record<string, unknown>
          | null
          | undefined;
        setViewConfig(cfg ?? null);
      } catch (err) {
        const friendly = UserFriendlyError.fromAny(err);
        setConfigError(friendly.message ?? 'Failed to load custom app config');
        setViewConfig(null);
      } finally {
        setIsLoadingConfig(false);
      }
    },
    [graphqlService, workspaceId]
  );

  useEffect(() => {
    if (allowed && selectedViewId) {
      loadViewerState(selectedViewId);
    }
  }, [allowed, selectedViewId, loadViewerState]);

  const selectedView = useMemo(
    () => views?.find(v => v.id === selectedViewId) ?? null,
    [views, selectedViewId]
  );
  const counts = useMemo(
    () => (selectedViewId ? countComponentsByType(viewConfig) : null),
    [selectedViewId, viewConfig]
  );
  const totalCount = counts
    ? Object.values(counts).reduce((sum, n) => sum + n, 0)
    : 0;

  if (!allowed) return null;

  const sectionTitle =
    t['com.streamlain.settings.workspace.customApps.components.title']();

  return (
    <SettingWrapper title={sectionTitle}>
      <SettingRow
        name={t[
          'com.streamlain.settings.workspace.customApps.components.beta.label'
        ]()}
        desc={t[
          'com.streamlain.settings.workspace.customApps.components.beta.desc'
        ]()}
      />

      <SettingRow
        name={t[
          'com.streamlain.settings.workspace.customApps.components.editorPointer.label'
        ]()}
        desc={t[
          'com.streamlain.settings.workspace.customApps.components.editorPointer.desc'
        ]()}
      />

      {isLoading ? (
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.components.loading'
          ]()}
          desc=""
        />
      ) : error ? (
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.components.error'
          ]()}
          desc={error}
        />
      ) : !views || views.length === 0 ? (
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.components.empty.title'
          ]()}
          desc={t[
            'com.streamlain.settings.workspace.customApps.components.empty.desc'
          ]()}
        />
      ) : (
        <>
          {views.length > 1 ? (
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.customApps.components.selector.label'
              ]()}
              desc={t[
                'com.streamlain.settings.workspace.customApps.components.selector.desc'
              ]()}
            >
              <Menu
                items={views.map(view => (
                  <MenuItem
                    key={view.id}
                    onClick={() => setSelectedViewId(view.id)}
                    selected={view.id === selectedViewId}
                  >
                    {view.title?.length ? view.title : view.slug}
                  </MenuItem>
                ))}
              >
                <Button>
                  {selectedView?.title?.length
                    ? selectedView.title
                    : (selectedView?.slug ?? '—')}
                </Button>
              </Menu>
            </SettingRow>
          ) : null}

          {isLoadingConfig ? (
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.customApps.components.loadingConfig'
              ]()}
              desc=""
            />
          ) : configError ? (
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.customApps.components.configError'
              ]()}
              desc={configError}
            />
          ) : counts ? (
            <>
              <SettingRow
                name={t[
                  'com.streamlain.settings.workspace.customApps.components.summary.label'
                ]()}
                desc={String(totalCount)}
              />
              {KNOWN_COMPONENT_TYPES.map(type => (
                <SettingRow
                  key={type}
                  name={
                    t[
                      `com.streamlain.settings.workspace.customApps.components.type.${type}` as
                        | 'com.streamlain.settings.workspace.customApps.components.type.cta_button'
                        | 'com.streamlain.settings.workspace.customApps.components.type.embed_video'
                        | 'com.streamlain.settings.workspace.customApps.components.type.faq_block'
                    ]?.() ?? type
                  }
                  desc={String(counts[type])}
                />
              ))}
            </>
          ) : null}
        </>
      )}
    </SettingWrapper>
  );
};
